import React, { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import DashLayout from "@/components/DashLayout";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { Library as LibraryIcon, Plus, Trash2, Undo2, Search, AlertCircle } from "lucide-react";
import { toast } from "sonner";

const CLASSES = ["Nursery","LKG","UKG","1","2","3","4","5","6","7","8","9","10","11","12"];
const CATEGORIES = ["general","fiction","science","math","history","reference","comics","biography"];

const today = () => new Date().toISOString().slice(0, 10);
const plusDays = (n) => new Date(Date.now() + n * 86400000).toISOString().slice(0, 10);

/** Cascading Class → Section → Student picker (section-wise filtering) */
export function StudentPicker({ value, onChange, testPrefix = "sp" }) {
  const [cls, setCls] = useState("");
  const [sec, setSec] = useState("all");
  const [students, setStudents] = useState([]);

  useEffect(() => {
    if (!cls) { setStudents([]); return; }
    api.get("/students", { params: { class_name: cls } })
      .then((r) => setStudents(r.data))
      .catch(() => setStudents([]));
  }, [cls]);

  const sections = useMemo(() => [...new Set(students.map((s) => s.section).filter(Boolean))].sort(), [students]);
  const filtered = useMemo(() => sec === "all" ? students : students.filter((s) => s.section === sec), [students, sec]);

  return (
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
      <div>
        <Label className="mb-1 block text-xs">Class</Label>
        <Select value={cls} onValueChange={(v) => { setCls(v); setSec("all"); onChange(""); }}>
          <SelectTrigger data-testid={`${testPrefix}-class`}><SelectValue placeholder="Class" /></SelectTrigger>
          <SelectContent>{CLASSES.map((c) => <SelectItem key={c} value={c} data-testid={`${testPrefix}-class-${c}`}>Class {c}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <div>
        <Label className="mb-1 block text-xs">Section</Label>
        <Select value={sec} onValueChange={(v) => { setSec(v); onChange(""); }} disabled={!cls}>
          <SelectTrigger data-testid={`${testPrefix}-section`}><SelectValue placeholder="All" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all" data-testid={`${testPrefix}-section-all`}>All Sections</SelectItem>
            {sections.map((s) => <SelectItem key={s} value={s} data-testid={`${testPrefix}-section-${s}`}>Section {s}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div>
        <Label className="mb-1 block text-xs">Student</Label>
        <Select value={value} onValueChange={onChange} disabled={!cls}>
          <SelectTrigger data-testid={`${testPrefix}-student`}><SelectValue placeholder={cls ? `${filtered.length} students` : "Pick class first"} /></SelectTrigger>
          <SelectContent className="max-h-72">
            {filtered.map((s) => <SelectItem key={s.id} value={s.id} data-testid={`${testPrefix}-student-${s.id}`}>{s.first_name} {s.last_name} · {s.section || "—"}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
    </div>
  );
}

export default function Library() {
  const { user } = useAuth();
  const isAdmin = ["school_admin", "super_admin"].includes(user?.role);
  const [tab, setTab] = useState("issue");
  const [books, setBooks] = useState([]);
  const [issues, setIssues] = useState([]);
  const [q, setQ] = useState("");
  const [issueFilter, setIssueFilter] = useState("issued");
  const [bForm, setBForm] = useState({ title: "", author: "", isbn: "", category: "general", total_copies: 1, shelf: "" });
  const [iForm, setIForm] = useState({ book_id: "", student_id: "", due_date: plusDays(14) });
  const [fineFor, setFineFor] = useState(null);
  const [fine, setFine] = useState("0");

  const loadBooks = async () => {
    try {
      const { data } = await api.get("/erp/library/books", { params: q ? { q } : {} });
      setBooks(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Load failed"); }
  };
  const loadIssues = async () => {
    try {
      const { data } = await api.get("/erp/library/issues", { params: issueFilter === "all" ? {} : { status: issueFilter } });
      setIssues(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  useEffect(() => { loadBooks(); /* eslint-disable-next-line */ }, [q]);
  useEffect(() => { loadIssues(); /* eslint-disable-next-line */ }, [issueFilter]);

  const addBook = async () => {
    if (!bForm.title) return toast.error("Title required");
    try {
      await api.post("/erp/library/books", { ...bForm, total_copies: Number(bForm.total_copies) || 1 });
      toast.success("Book added");
      setBForm({ title: "", author: "", isbn: "", category: "general", total_copies: 1, shelf: "" });
      loadBooks();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const issue = async () => {
    if (!iForm.book_id || !iForm.student_id) return toast.error("Select book and student");
    try {
      await api.post("/erp/library/issues", iForm);
      toast.success("Book issued");
      setIForm({ ...iForm, student_id: "" });
      loadIssues(); loadBooks();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const doReturn = async (id) => {
    try {
      await api.put(`/erp/library/issues/${id}/return?fine=${Number(fine) || 0}`);
      toast.success("Book returned");
      setFineFor(null); setFine("0");
      loadIssues(); loadBooks();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const availableBooks = useMemo(() => books.filter((b) => (b.available ?? 0) > 0), [books]);
  const overdue = useMemo(() => issues.filter((i) => i.status === "issued" && i.due_date < today()).length, [issues]);
  const stats = useMemo(() => ({
    titles: books.length,
    copies: books.reduce((s, b) => s + (b.total_copies || 0), 0),
    issued: issues.filter((i) => i.status === "issued").length,
    overdue,
  }), [books, issues, overdue]);

  return (
    <DashLayout variant={isAdmin ? "school" : "teacher"} title="Library">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        <Kpi label="Titles" value={stats.titles} color="bg-blue-50 text-blue-700" testId="lib-kpi-titles" />
        <Kpi label="Total Copies" value={stats.copies} color="bg-indigo-50 text-indigo-700" testId="lib-kpi-copies" />
        <Kpi label="Issued Now" value={stats.issued} color="bg-emerald-50 text-emerald-700" testId="lib-kpi-issued" />
        <Kpi label="Overdue" value={stats.overdue} color="bg-red-50 text-red-700" testId="lib-kpi-overdue" />
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <div className="-mx-1 overflow-x-auto pb-1 mb-4">
          <TabsList className="inline-flex w-max min-w-full">
            <TabsTrigger value="issue" data-testid="tab-lib-issue">Issue / Return</TabsTrigger>
            <TabsTrigger value="books" data-testid="tab-lib-books">Books Inventory</TabsTrigger>
          </TabsList>
        </div>

        {/* ---------- ISSUE / RETURN ---------- */}
        <TabsContent value="issue">
          <Card className="p-4 mb-4" data-testid="lib-issue-form">
            <h3 className="font-bold text-sm mb-3">Issue a Book — pick Class → Section → Student</h3>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
              <div className="md:col-span-1">
                <Label className="mb-1 block text-xs">Book (available)</Label>
                <Select value={iForm.book_id} onValueChange={(v) => setIForm({ ...iForm, book_id: v })}>
                  <SelectTrigger data-testid="lib-issue-book"><SelectValue placeholder="Select book" /></SelectTrigger>
                  <SelectContent className="max-h-72">
                    {availableBooks.map((b) => <SelectItem key={b.id} value={b.id} data-testid={`lib-issue-book-${b.id}`}>{b.title} ({b.available} left)</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="md:col-span-2">
                <StudentPicker value={iForm.student_id} onChange={(v) => setIForm({ ...iForm, student_id: v })} testPrefix="lib-issue" />
              </div>
              <div>
                <Label className="mb-1 block text-xs">Due Date</Label>
                <Input type="date" value={iForm.due_date} onChange={(e) => setIForm({ ...iForm, due_date: e.target.value })} data-testid="lib-issue-due" />
              </div>
            </div>
            <Button onClick={issue} className="mt-3 bg-blue-700 hover:bg-blue-800" data-testid="lib-issue-submit"><Plus className="w-4 h-4 mr-1" /> Issue Book</Button>
          </Card>

          <Card className="p-3 mb-3 flex flex-wrap items-center gap-3">
            <Select value={issueFilter} onValueChange={setIssueFilter}>
              <SelectTrigger className="w-40" data-testid="lib-filter-status"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="issued">Issued</SelectItem>
                <SelectItem value="returned">Returned</SelectItem>
                <SelectItem value="all">All</SelectItem>
              </SelectContent>
            </Select>
            <div className="text-xs text-slate-500 ml-auto">{issues.length} records</div>
          </Card>

          <Card className="p-0 overflow-hidden">
            <div className="hidden md:block">
              <Table>
                <TableHeader><TableRow>
                  <TableHead>Book</TableHead><TableHead>Student</TableHead><TableHead>Class</TableHead>
                  <TableHead>Issued</TableHead><TableHead>Due</TableHead><TableHead>Status</TableHead>
                  <TableHead className="text-right">Fine</TableHead><TableHead className="text-right">Action</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {issues.map((i) => {
                    const late = i.status === "issued" && i.due_date < today();
                    return (
                      <TableRow key={i.id} data-testid={`lib-issue-row-${i.id}`}>
                        <TableCell className="font-semibold">{i.book_title}</TableCell>
                        <TableCell>{i.student_name}</TableCell>
                        <TableCell>{i.class_name}{i.section ? ` ${i.section}` : ""}</TableCell>
                        <TableCell className="font-mono text-xs">{i.issue_date}</TableCell>
                        <TableCell className={`font-mono text-xs ${late ? "text-red-700 font-bold" : ""}`}>{i.due_date}{late && " ⚠"}</TableCell>
                        <TableCell><Badge variant="outline" className={i.status === "returned" ? "border-emerald-300 text-emerald-700" : late ? "border-red-300 text-red-700" : "border-blue-300 text-blue-700"}>{late ? "overdue" : i.status}</Badge></TableCell>
                        <TableCell className="text-right tabular-nums">{i.fine ? `₹${i.fine}` : "—"}</TableCell>
                        <TableCell className="text-right">
                          {i.status === "issued" && (
                            fineFor === i.id ? (
                              <span className="inline-flex gap-1 items-center">
                                <Input type="number" className="w-20 h-8" value={fine} onChange={(e) => setFine(e.target.value)} placeholder="Fine ₹" data-testid={`lib-fine-${i.id}`} />
                                <Button size="sm" className="bg-emerald-700 hover:bg-emerald-800" onClick={() => doReturn(i.id)} data-testid={`lib-return-confirm-${i.id}`}>OK</Button>
                              </span>
                            ) : (
                              <Button size="sm" variant="outline" onClick={() => setFineFor(i.id)} data-testid={`lib-return-${i.id}`}><Undo2 className="w-4 h-4 mr-1" /> Return</Button>
                            )
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  {issues.length === 0 && <TableRow><TableCell colSpan={8} className="text-center py-10 text-slate-500">No {issueFilter} records</TableCell></TableRow>}
                </TableBody>
              </Table>
            </div>
            <div className="md:hidden divide-y divide-slate-100">
              {issues.map((i) => {
                const late = i.status === "issued" && i.due_date < today();
                return (
                  <div key={i.id} className="p-4" data-testid={`lib-issue-card-${i.id}`}>
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <div className="font-semibold">{i.book_title}</div>
                        <div className="text-xs text-slate-500">{i.student_name} · Class {i.class_name}{i.section ? ` ${i.section}` : ""}</div>
                      </div>
                      <Badge variant="outline" className={i.status === "returned" ? "border-emerald-300 text-emerald-700" : late ? "border-red-300 text-red-700" : "border-blue-300 text-blue-700"}>{late ? "overdue" : i.status}</Badge>
                    </div>
                    <div className="text-xs text-slate-500 mt-2 font-mono">Issued {i.issue_date} · Due {i.due_date}{i.fine ? ` · Fine ₹${i.fine}` : ""}</div>
                    {i.status === "issued" && (
                      fineFor === i.id ? (
                        <div className="flex gap-2 mt-3">
                          <Input type="number" value={fine} onChange={(e) => setFine(e.target.value)} placeholder="Fine ₹" data-testid={`lib-card-fine-${i.id}`} />
                          <Button size="sm" className="bg-emerald-700 hover:bg-emerald-800 flex-1" onClick={() => doReturn(i.id)} data-testid={`lib-card-return-confirm-${i.id}`}>Confirm Return</Button>
                        </div>
                      ) : (
                        <Button size="sm" variant="outline" className="w-full mt-3" onClick={() => setFineFor(i.id)} data-testid={`lib-card-return-${i.id}`}><Undo2 className="w-4 h-4 mr-1" /> Return Book</Button>
                      )
                    )}
                  </div>
                );
              })}
              {issues.length === 0 && <div className="p-10 text-center text-slate-500 text-sm">No {issueFilter} records</div>}
            </div>
          </Card>
        </TabsContent>

        {/* ---------- BOOKS ---------- */}
        <TabsContent value="books">
          {isAdmin && (
            <Card className="p-4 grid grid-cols-2 md:grid-cols-6 gap-2 items-end mb-4" data-testid="lib-book-form">
              <div className="col-span-2"><Label className="mb-1 block text-xs">Title *</Label><Input value={bForm.title} onChange={(e) => setBForm({ ...bForm, title: e.target.value })} data-testid="lib-book-title" /></div>
              <div><Label className="mb-1 block text-xs">Author</Label><Input value={bForm.author} onChange={(e) => setBForm({ ...bForm, author: e.target.value })} data-testid="lib-book-author" /></div>
              <div><Label className="mb-1 block text-xs">Category</Label>
                <Select value={bForm.category} onValueChange={(v) => setBForm({ ...bForm, category: v })}>
                  <SelectTrigger data-testid="lib-book-category"><SelectValue /></SelectTrigger>
                  <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label className="mb-1 block text-xs">Copies</Label><Input type="number" min="1" value={bForm.total_copies} onChange={(e) => setBForm({ ...bForm, total_copies: e.target.value })} data-testid="lib-book-copies" /></div>
              <Button onClick={addBook} className="bg-blue-700 hover:bg-blue-800 col-span-2 md:col-span-1" data-testid="lib-book-add"><Plus className="w-4 h-4 mr-1" /> Add Book</Button>
            </Card>
          )}
          <Card className="p-3 mb-3">
            <div className="relative">
              <Search className="w-4 h-4 absolute left-2 top-2.5 text-slate-400" />
              <Input placeholder="Search title / author / ISBN" value={q} onChange={(e) => setQ(e.target.value)} className="pl-8" data-testid="lib-book-search" />
            </div>
          </Card>
          <Card className="p-0 overflow-hidden">
            <div className="hidden md:block">
              <Table>
                <TableHeader><TableRow>
                  <TableHead>Title</TableHead><TableHead>Author</TableHead><TableHead>Category</TableHead>
                  <TableHead className="text-right">Copies</TableHead><TableHead className="text-right">Available</TableHead>
                  {isAdmin && <TableHead className="text-right">—</TableHead>}
                </TableRow></TableHeader>
                <TableBody>
                  {books.map((b) => (
                    <TableRow key={b.id} data-testid={`lib-book-row-${b.id}`}>
                      <TableCell className="font-semibold">{b.title}</TableCell>
                      <TableCell>{b.author || "—"}</TableCell>
                      <TableCell><Badge variant="outline">{b.category}</Badge></TableCell>
                      <TableCell className="text-right tabular-nums">{b.total_copies}</TableCell>
                      <TableCell className={`text-right tabular-nums font-bold ${b.available === 0 ? "text-red-700" : "text-emerald-700"}`}>{b.available}</TableCell>
                      {isAdmin && <TableCell className="text-right"><Button size="sm" variant="ghost" onClick={async () => { await api.delete(`/erp/library/books/${b.id}`); loadBooks(); }} data-testid={`lib-book-del-${b.id}`}><Trash2 className="w-4 h-4 text-red-600" /></Button></TableCell>}
                    </TableRow>
                  ))}
                  {books.length === 0 && <TableRow><TableCell colSpan={6} className="text-center py-10 text-slate-500"><AlertCircle className="w-5 h-5 mx-auto mb-2 opacity-40" />No books yet — add your first book above</TableCell></TableRow>}
                </TableBody>
              </Table>
            </div>
            <div className="md:hidden divide-y divide-slate-100">
              {books.map((b) => (
                <div key={b.id} className="p-4 flex items-center justify-between gap-2" data-testid={`lib-book-card-${b.id}`}>
                  <div className="min-w-0">
                    <div className="font-semibold truncate">{b.title}</div>
                    <div className="text-xs text-slate-500">{b.author || "—"} · {b.category}</div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className={`text-sm font-bold tabular-nums ${b.available === 0 ? "text-red-700" : "text-emerald-700"}`}>{b.available}/{b.total_copies}</div>
                    <div className="text-[10px] text-slate-400">available</div>
                  </div>
                </div>
              ))}
              {books.length === 0 && <div className="p-10 text-center text-slate-500 text-sm">No books yet</div>}
            </div>
          </Card>
        </TabsContent>
      </Tabs>
    </DashLayout>
  );
}

const Kpi = ({ label, value, color, testId }) => (
  <Card className="p-3" data-testid={testId}>
    <div className={`inline-flex px-2 py-0.5 rounded text-[10px] uppercase tracking-wide ${color}`}>{label}</div>
    <div className="text-2xl font-black mt-1 tabular-nums">{value}</div>
  </Card>
);
